import { ComponentType, useEffect, useRef, useState } from "react";
import { VariantProps } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowDownTray } from "react-icons/hi2";

import { CsvUploaderError, useCsvUploader } from "@/hooks/useCsvUploader";
import useTierLimit from "@/hooks/useTierLimit";
import Button from "@/components/atoms/Button";
import Modal, { modalSizes } from "@/components/molecules/Modal";
import EmptyFileModal from "@/components/molecules/modals/file-upload/EmptyFileModal";
import FileLimitExceededModal from "@/components/molecules/modals/file-upload/FileLimitExceededModal";
import MapColumnsModal from "@/components/molecules/modals/file-upload/MapColumnsModal";
import RowLimitExceededModal from "@/components/molecules/modals/file-upload/RowLimitExceededModal";

type ModalPropsMap = {
  [key: string]: VariantProps<typeof modalSizes> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content: ComponentType<any>;
    contentProps: Record<string, unknown>;
  };
};

type Props = {
  file?: File | null;
};
// TODO Adapt the rendering of multiple modals in the future. Prevent flashing of modals and improve type safety.
const EnrichModalHandler = ({ file }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentModalType, setCurrentModalType] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { tierLimit } = useTierLimit();

  useEffect(() => {
    if (file) {
      processFile(file);
    }
  }, [file]);

  const {
    filename,
    guessedColumnsMapping,
    columnNames,
    mapColumns,
    error,
    mappedData,
    validRowCount,
    processFile,
    isProcessing,
    resetError,
  } = useCsvUploader({
    rowLimit: tierLimit,
    t,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetError();
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const modalMapping: ModalPropsMap = {
    [CsvUploaderError.EMPTY_FILE]: {
      content: EmptyFileModal,
      size: "sm",
      contentProps: {
        onFileUpload: (file: File) => processFile(file),
      },
    },
    [CsvUploaderError.FILE_LIMIT_EXCEEDED]: {
      content: FileLimitExceededModal,
      size: "sm",
      contentProps: {
        onFileUpload: (file: File) => processFile(file),
      },
    },
    [CsvUploaderError.ROW_LIMIT_EXCEEDED]: {
      content: RowLimitExceededModal,
      size: "sm",
      contentProps: {
        limit: tierLimit,
        onSuccess: () => resetError(),
      },
    },
    NO_ERROR: {
      content: MapColumnsModal,
      contentProps: {},
      size: "xl",
      padding: "none",
    },
  };

  useEffect(() => {
    if (error) {
      setCurrentModalType(error);
      setIsOpen(true);
    } else if (!isProcessing && filename) {
      setCurrentModalType("NO_ERROR");
      setIsOpen(true);
    }
    // Do not open the modal if neither error nor filename is set
  }, [error, isProcessing, filename]);

  const getDynamicContentProps = () => {
    if (currentModalType === "NO_ERROR") {
      return {
        listName: filename,
        columns: columnNames,
        validRowCount: validRowCount,
        guessedColumnsMapping: guessedColumnsMapping,
        mapColumns: mapColumns,
        mappedData: mappedData,
        onSuccess: () => setIsOpen(false),
      };
    }
    if (currentModalType !== null) {
      return modalMapping[currentModalType]?.contentProps || {};
    }
    return {};
  };

  const currentModalConfig = currentModalType && modalMapping[currentModalType] ? modalMapping[currentModalType] : null;

  return (
    <>
      <input type="file" accept=".csv" className="hidden" ref={fileInputRef} onChange={handleFileChange} />
      <Button
        onClick={handleButtonClick}
        trailingIcon={<HiOutlineArrowDownTray className="size-5" />}
        label={t("enrich.import")}
      />
      {currentModalConfig && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setCurrentModalType(null);
          }}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          content={currentModalType === "NO_ERROR" ? MapColumnsModal : currentModalConfig.content}
          contentProps={getDynamicContentProps()}
          size={currentModalConfig.size}
          padding={currentModalConfig.padding}
        />
      )}
    </>
  );
};

export default EnrichModalHandler;
